import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Space, Avatar, Dropdown, Typography } from 'antd';
import { 
  MenuFoldOutlined, 
  MenuUnfoldOutlined,
  RobotOutlined,
  FolderOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  ApiOutlined,
  SettingOutlined,
  NotificationOutlined,
  BookOutlined,
  DatabaseOutlined,
  FilePdfOutlined,
  SafetyOutlined,
  LinkOutlined,
  CalculatorOutlined,
  WechatOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { BRAND, NEUTRAL, SHADOW } from '../constants/theme';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout = ({ children, showHeader = true, showSider = true }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '首页',
      onClick: () => navigate('/'),
    },
    {
      key: 'agent',
      icon: <ApiOutlined />,
      label: '智能体工作台',
      children: [
        {
          key: '/agent/scheme_reviewer',
          icon: <RobotOutlined />,
          label: '方案审核助手',
          onClick: () => navigate('/agent/scheme_reviewer'),
        },
        {
          key: '/agent/scheme_writer',
          icon: <RobotOutlined />,
          label: '方案编写助手',
          onClick: () => navigate('/agent/scheme_writer'),
        },
        {
          key: '/agent/qa_assistant',
          icon: <RobotOutlined />,
          label: '智能问答助手',
          onClick: () => navigate('/agent/qa_assistant'),
        },
      ],
    },
    {
      key: '/projects',
      icon: <FolderOutlined />,
      label: '项目管理',
      onClick: () => navigate('/projects'),
    },
    {
      key: 'industry',
      icon: <FileTextOutlined />,
      label: '行业数据',
      children: [
        {
          key: '/industry/cases',
          icon: <DatabaseOutlined />,
          label: '工程案例',
          onClick: () => navigate('/industry/cases'),
        },
        {
          key: '/industry/standards',
          icon: <BookOutlined />,
          label: '技术标准',
          onClick: () => navigate('/industry/standards'),
        },
        {
          key: '/industry/documents',
          icon: <FilePdfOutlined />,
          label: '规范文档',
          onClick: () => navigate('/industry/documents'),
        },
        {
          key: '/industry/policies',
          icon: <SafetyOutlined />,
          label: '政策法规',
          onClick: () => navigate('/industry/policies'),
        },
        {
          key: '/industry/websites',
          icon: <LinkOutlined />,
          label: '重点网站',
          onClick: () => navigate('/industry/websites'),
        },
      ],
    },
    {
      key: '/experts',
      icon: <UserOutlined />,
      label: '专家智库',
      onClick: () => navigate('/experts'),
    },
    {
      key: 'calculator',
      icon: <CalculatorOutlined />,
      label: '计算工具',
      children: [
        {
          key: '/calculator/erosion',
          icon: <CalculatorOutlined />,
          label: '水土流失量预测',
          onClick: () => navigate('/calculator/erosion'),
        },
        {
          key: '/calculator/dam',
          icon: <CalculatorOutlined />,
          label: '拦渣坝参数计算',
          onClick: () => navigate('/calculator/dam'),
        },
        {
          key: '/calculator/benefit',
          icon: <CalculatorOutlined />,
          label: '措施效益评估',
          onClick: () => navigate('/calculator/benefit'),
        },
        {
          key: '/calculator/topsoil',
          icon: <CalculatorOutlined />,
          label: '表土剥离回用',
          onClick: () => navigate('/calculator/topsoil'),
        },
      ],
    },
    {
      key: 'skills',
      icon: <RobotOutlined />,
      label: '技能中心',
      children: [
        {
          key: 'skillhub_water',
          icon: <LinkOutlined />,
          label: '水土保持审核技能',
          onClick: () => window.open('https://skillhub.cloud.tencent.com/skills/water-conservation-review', '_blank'),
        },
        {
          key: 'skillhub_more',
          icon: <SettingOutlined />,
          label: '更多技能',
          onClick: () => navigate('/skills'),
        },
      ],
    },
    {
      key: 'wechat',
      icon: <WechatOutlined />,
      label: '技术支持',
      onClick: () => window.open('https://work.weixin.qq.com/ca/cawcde134b484edca8', '_blank'),
    },
  ];

  const getSelectedKeys = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/industry')) {
      return [pathname];
    }
    if (pathname.startsWith('/agent')) {
      return [pathname];
    }
    return [pathname];
  };

  const getOpenKeys = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/industry')) {
      return ['industry'];
    }
    if (pathname.startsWith('/agent')) {
      return ['agent'];
    }
    if (pathname.startsWith('/calculator')) {
      return ['calculator'];
    }
    if (pathname.startsWith('/skills')) {
      return ['skills'];
    }
    return [];
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {showHeader && (
        <Header style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: BRAND.HEADER,
          padding: '0 24px',
          boxShadow: SHADOW.HEADER,
          zIndex: 1000,
          position: 'fixed',
          width: '100%',
          top: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {showSider && (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ color: 'white', fontSize: '16px' }}
              />
            )}
            <div style={{ 
              color: 'white', 
              fontSize: '20px', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ fontSize: '24px' }}>💧</span>
              水保数联
            </div>
          </div>
          
          <Space size="large">
            <Button
              type="text"
              icon={<NotificationOutlined />}
              style={{ color: 'white', fontSize: '16px' }}
            />
            {user ? (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transition: 'all 0.3s',
                }}>
                  <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: BRAND.PRIMARY }} />
                  <Text style={{ color: 'white', fontSize: '14px' }}>{user.username}</Text>
                </div>
              </Dropdown>
            ) : (
              <Space>
                <Button 
                  type="text" 
                  onClick={() => navigate('/login')} 
                  style={{ color: 'white', fontSize: '14px' }}
                >
                  登录
                </Button>
                <Button 
                  type="primary" 
                  onClick={() => navigate('/register')}
                >
                  注册
                </Button>
              </Space>
            )}
          </Space>
        </Header>
      )}

      <Layout style={{ marginTop: showHeader ? '64px' : 0 }}>
        {showSider && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            width={240}
            style={{ 
              backgroundColor: NEUTRAL.BG_CARD,
              overflow: 'hidden',
              height: 'calc(100vh - 64px)',
              position: 'fixed',
              left: 0,
              boxShadow: SHADOW.SIDER,
              display: 'flex',
              flexDirection: 'column',
            }}
            trigger={null}
          >
            <div style={{ flex: 1, overflow: 'auto' }}>
              <Menu
                mode="inline"
                selectedKeys={getSelectedKeys()}
                defaultOpenKeys={getOpenKeys()}
                items={menuItems}
                style={{ 
                  borderRight: 0,
                  paddingTop: '16px',
                }}
              />
            </div>
            <div style={{ 
              padding: '12px 16px', 
              borderTop: `1px solid ${NEUTRAL.BORDER}`,
              textAlign: 'center',
            }}>
              <div style={{ 
                color: NEUTRAL.TEXT_COPYRIGHT, 
                fontSize: '11px', 
                fontWeight: '300',
                letterSpacing: '0.5px',
                lineHeight: '1.6',
              }}>
                <div>与志同道合者共事，行心之所向之路</div>
                <div style={{ marginTop: '2px' }}>技术支持：天津创锐丰科技有限公司</div>
              </div>
            </div>
          </Sider>
        )}

        <Layout style={{ 
          marginLeft: showSider ? (collapsed ? '80px' : '240px') : 0,
          transition: 'margin-left 0.2s',
        }}>
          <Content style={{ 
            backgroundColor: NEUTRAL.BG_PAGE,
            minHeight: 'calc(100vh - 64px)',
          }}>
            <div style={{ padding: '24px', minHeight: '100%' }}>
              {children}
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
